import { useEffect, useState } from 'react';
import { C2Button } from '../c2';
import { waitForExtension, waitForSignInState } from '../../lib/extensionPresence';

interface EmptyStateProps {
  onCreate: (suggestedSlug?: string) => void;
}

const STARTERS: Array<{ slug: string; desc: string }> = [
  { slug: 'handbook', desc: 'Company handbook' },
  { slug: 'roadmap', desc: 'Product roadmap' },
  { slug: 'oncall', desc: 'On-call schedule' },
  { slug: 'newhire', desc: 'Onboarding doc' },
];

export default function EmptyState({ onCreate }: EmptyStateProps) {
  // Show the extension install button only when this browser doesn't have a
  // working extension — either not installed at all, or installed but not
  // signed in. The dashboard's gate normally redirects users in that state to
  // /onboarding?fix=extension before they reach this page, but old extensions
  // (≤v3.5) can't report sign-in state, so the gate lets them through and we
  // catch it here instead. Default false so the button stays hidden during
  // the initial async detection.
  const [needsExtension, setNeedsExtension] = useState(false);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const installed = await waitForExtension(1500);
      if (cancelled) return;
      if (!installed) { setNeedsExtension(true); return; }
      const signedIn = await waitForSignInState(500);
      if (cancelled) return;
      if (signedIn === 'no') setNeedsExtension(true);
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div
      className="mx-auto text-center bg-surface relative overflow-hidden"
      style={{
        margin: '60px auto 0',
        maxWidth: 640,
        padding: '60px 40px',
        border: '1px solid var(--color-hairline)',
        borderRadius: 28,
      }}
    >
      <div
        className="absolute"
        style={{ top: -60, right: -60, width: 180, height: 180, borderRadius: '50%', background: 'var(--color-peach)', opacity: 0.6 }}
      />
      <div
        className="absolute"
        style={{ bottom: -40, left: -40, width: 120, height: 120, borderRadius: '50%', background: 'var(--color-mint)', opacity: 0.6 }}
      />

      <div className="relative">
        <div
          className="mx-auto flex items-center justify-center font-mono font-extrabold"
          style={{
            width: 88,
            height: 88,
            borderRadius: 20,
            margin: '0 auto 24px',
            background: 'var(--accent-soft)',
            color: 'var(--accent-deep)',
            fontSize: 28,
            border: '1px solid color-mix(in oklch, var(--accent-solid) 13%, transparent)',
            boxShadow: '0 8px 24px -10px color-mix(in oklch, var(--accent-solid) 33%, transparent)',
          }}
        >
          go/
        </div>

        <h2
          className="font-display font-bold text-ink m-0 mb-2.5"
          style={{ fontSize: 32, letterSpacing: '-0.02em' }}
        >
          Your workspace is ready.
        </h2>
        <p className="text-base text-ink-soft leading-[1.5] mx-auto m-0 mb-7" style={{ maxWidth: 440 }}>
          Create your first go/ link and watch the whole team stop pasting URLs in Slack.
        </p>

        <div className="flex gap-2.5 justify-center flex-wrap">
          <C2Button variant="accent" size="md" onClick={() => onCreate()}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
              <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Create your first go/ link
          </C2Button>
          {needsExtension && (
            <a href="/onboarding?fix=extension" className="no-underline">
              <C2Button variant="outline" size="md">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                Install Chrome extension
              </C2Button>
            </a>
          )}
        </div>

        <div
          className="bg-bg-alt text-left"
          style={{ marginTop: 40, padding: 20, borderRadius: 14 }}
        >
          <div
            className="font-bold uppercase mb-3 text-ink-muted"
            style={{ fontSize: 12, letterSpacing: '0.06em' }}
          >
            Teams usually start with
          </div>
          <div className="grid grid-cols-2 gap-2">
            {STARTERS.map(s => (
              <button
                key={s.slug}
                onClick={() => onCreate(s.slug)}
                className="flex items-center gap-2.5 bg-surface cursor-pointer text-left transition-all duration-150"
                style={{
                  padding: '10px 12px',
                  border: '1px solid var(--color-hairline)',
                  borderRadius: 8,
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.borderColor = 'var(--accent-solid)';
                  e.currentTarget.style.background = 'var(--accent-tint)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.borderColor = 'var(--color-hairline)';
                  e.currentTarget.style.background = 'var(--color-surface)';
                }}
              >
                <span className="font-mono font-semibold text-ink text-[13px]">
                  <span className="text-ink-muted">go/</span>{s.slug}
                </span>
                <span className="text-[11.5px] text-ink-muted ml-auto">{s.desc}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
